import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Table, DatePicker, Select, Spin, Row, Col } from 'antd'
import { ArrowDownOutlined, ArrowUpOutlined, CaretDownOutlined } from '@ant-design/icons'
import numeral from 'numeral'
import moment from 'moment'
import { getInsights } from '../../../../HttpRequests/insights'
import TimesheetsDrawer from '../../../../Modules/TimesheetsDrawer'

const { RangePicker } = DatePicker

const footerStyles = {
  footer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 25,
    borderTop: '1px solid #DDD'
  },
  total: {
    marginRight: 40
  },
  label: {
    fontSize: 12,
    fontWeight: 500,
    color: '#999'
  },
  value: {
    fontSize: 15,
    fontWeight: 500,
    color: '#444'
  }
}

const SubcontractorTimesheets = ({ projectId }) => {

  const [data, setData] = useState([])
  const [fetching, setFetching] = useState(false)
  const [sortBy, setSortBy] = useState('hours_worked.desc')
  const [hoursType, setHoursType] = useState('all')
  const [hoursFormat, setHoursFormat] = useState('decimal')
  const [dateRange, setDateRange] = useState([])
  const [timesheetsDrawer, setTimesheetsDrawer] = useState({ show: false })

  const fetchData = async () => {
    setFetching(true)

    const params = {
      model: 'Timesheet',
      field: 'subcontractor_id',
      filter_project_id: projectId,
      sort_by: sortBy,
    }

    if (dateRange.length === 2) {
      params.filter_date_range = dateRange.map(date => moment(date).format('YYYY-MM-DD')).join()
    }

    if (hoursType === 'hourly-rate') {
      params.filter_hourly_rate = 1
    }
    if (hoursType === 'day-rate') {
      params.filter_day_rate = 1
    }
    if (hoursType === 'price-work') {
      params.filter_price_work = 1
    }

    try {
      const response = await getInsights({ params })
      setData(response.data.data || [])
    } catch (error) {
      console.error('Error fetching subcontractor timesheets:', error)
    } finally {
      setFetching(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [projectId, sortBy, dateRange, hoursType])

  const onChangeSort = (key) => {
    setSortBy(key)
  }

  const onClickRow = (record) => {
    // Build filters object matching the structure expected by TimesheetsDrawer
    const filters = {
      subcontractorId: [],
      projectId: [projectId],
      contractsManagerId: [],
      dateRange: dateRange.length === 2 ? dateRange : [],
      hoursType: hoursType,
      hoursFormat: hoursFormat,
      _subcontractorId: record.key, // This is the specific subcontractor to filter by
      _projectId: projectId,
    }

    setTimesheetsDrawer({
      show: true,
      title: record.field,
      filters: filters,
    })
  }

  const onCloseTimesheetsDrawer = () => {
    setTimesheetsDrawer({ show: false })
  }

  const columns = [
    {
      title: 'Subcontractor',
      key: 'field',
      width: 250,
      render: (text, record) => (
        <div>
          <div style={{ fontSize: 18, fontWeight: 500 }}>
            {record.field}
          </div>
          {record.h2 && <div style={{ fontSize: 16, fontWeight: 500 }}>{record.h2}</div>}
          {record.h3 && <div>{record.h3}</div>}
        </div>
      ),
    },
    {
      title: 'Postings',
      key: 'quantity',
      width: 150,
      align: 'right',
      render: (text, record) => (
        <div>
          <div>
            <strong style={{ fontSize: '16px' }}>
              {record.quantity}
            </strong>
          </div>
        </div>
      )
    },
    {
      title: 'Hours',
      key: 'hours',
      width: 200,
      align: 'right',
      sorter: true,
      render: (text, record) => (
        <div>
          <div>
            <strong style={{ fontSize: '16px' }}>
              {hoursFormat === 'time' ? record.hours_formatted : record.hours}
            </strong>
          </div>
          <div style={{ color: 'orange' }}>
            Deductions:
            <strong>
              {` ${hoursFormat === 'time' ? record.deductions_formatted : record.deductions}`}
            </strong>
          </div>
          <div style={{ color: '#9e9e9e', fontStyle: 'italic' }}>
            Adjustments:
            <strong>
              {` ${hoursFormat === 'time' ? record.adjustments_formatted : record.adjustments}`}
            </strong>
          </div>
          <div>
            Total:
            <strong>
              {` ${hoursFormat === 'time' ? record.hours_worked_formatted : record.hours_worked}`}
            </strong>
          </div>
        </div>
      )
    },
    {
      title: 'Cost',
      key: 'cost',
      width: 200,
      align: 'right',
      render: (text, record) => (
        <div>
          <div>
            <strong style={{ fontSize: '16px' }}>
              {`£${numeral(record.total_cost).format('0,0.00')}`}
            </strong>
          </div>
          {hoursType !== 'price-work' && (
            <div>
              Avg per hour:
              <strong>
                {` £${numeral(record.avg_hourly_rate).format('0,0.00')}`}
              </strong>
            </div>
          )}
        </div>
      )
    },
  ]

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col>
          <RangePicker
            value={dateRange.length === 2 ? dateRange : undefined}
            onChange={(dates) => setDateRange(dates || [])}
            format="DD/MM/YYYY"
            placeholder={['Start Date', 'End Date']}
          />
        </Col>
        <Col>
          <Select
            value={hoursType}
            onChange={setHoursType}
            style={{ width: 150 }}
          >
            <Select.Option value="all">All Types</Select.Option>
            <Select.Option value="hourly-rate">Hourly Rate</Select.Option>
            <Select.Option value="day-rate">Day Rate</Select.Option>
            <Select.Option value="price-work">Price Work</Select.Option>
          </Select>
        </Col>
        <Col>
          <Select
            value={hoursFormat}
            onChange={setHoursFormat}
            style={{ width: 120 }}
          >
            <Select.Option value="decimal">Decimal</Select.Option>
            <Select.Option value="time">Time</Select.Option>
          </Select>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={data}
        rowKey='key'
        loading={fetching}
        onRow={record => ({
          onClick: () => onClickRow(record),
          style: { cursor: 'pointer' }
        })}
        pagination={{ pageSize: 20 }}
        scroll={{ x: columns.reduce((total, column) => total + (column.width || 150), 0) }}
      />

      {data.length > 0 && (
        <div style={footerStyles.footer}>
          <div style={footerStyles.total}>
            <div style={footerStyles.label}>Total Hours</div>
            <div style={footerStyles.value}>
              {data.reduce((sum, record) => sum + parseFloat(record.hours_worked || 0), 0).toFixed(2)}
            </div>
          </div>
          <div style={footerStyles.total}>
            <div style={footerStyles.label}>Total Postings</div>
            <div style={footerStyles.value}>
              {data.reduce((sum, record) => sum + (record.quantity || 0), 0)}
            </div>
          </div>
          <div style={footerStyles.total}>
            <div style={footerStyles.label}>Subcontractors</div>
            <div style={footerStyles.value}>
              {data.length}
            </div>
          </div>
          <div style={footerStyles.total}>
            <div style={footerStyles.label}>Total Cost</div>
            <div style={footerStyles.value}>
              {`£${numeral(data.reduce((sum, record) => sum + parseFloat(record.total_cost || 0), 0)).format('0,0.00')}`}
            </div>
          </div>
        </div>
      )}

      {timesheetsDrawer.show && (
        <TimesheetsDrawer
          {...timesheetsDrawer}
          onClose={onCloseTimesheetsDrawer}
        />
      )}
    </div>
  )
}

SubcontractorTimesheets.propTypes = {
  projectId: PropTypes.number.isRequired,
}

export default SubcontractorTimesheets
